/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Modal, ListGroup } from 'react-bootstrap'
import AddNoteFilter from './AddNoteFilter'
import { INote, NoteContextType } from '@dataTypes/note.type'

// Helper to map source strings to NoteContextType
const getContextType = (source: NoteContextType | string): NoteContextType => {
  if (typeof source === 'string') {
    const sourceMap: Record<string, NoteContextType> = {
      contact: NoteContextType.CONTACT,
      appointment: NoteContextType.APPOINTMENT,
      charting: NoteContextType.CHARTING,
      perio: NoteContextType.PERIO,
      treatmentPlan: NoteContextType.TREATMENT_PLAN,
      call: NoteContextType.CALL,
      claims: NoteContextType.CLAIMS,
      billing: NoteContextType.BILLING,
    }
    return sourceMap[source] || NoteContextType.PATIENT
  }
  return source
}

import {
  addNote,
  updateNote as updateNoteAction,
  removeNote,
  fetchAllNotes,
} from '@store/actions/note.action'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import './AddNoteStyle.css'
import { format } from 'date-fns'
import { ConfirmationModal, TagBadge, Button } from '@components/common'
import { deleteChartNote } from '@store/reducers/charting.reducer'
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons'
import NoteTextEditor, {
  getPreviewContentText,
} from '@components/common/NoteTextEditor/NoteTextEditor.component'
import { resolveNoteBodyBlobSrc } from '@services/api/note.api'
import {
  getContextItemDisplayName,
  type NoteFilterContextLookup,
} from '@utils/noteFilter.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'

import { logger } from '@utils/safeLogger'

interface AddNoteProps {
  show: boolean
  onHide: (hasNewOrUpdatedRecord?: boolean) => void
  source: NoteContextType | string
  contextType?: NoteContextType
  selectedRefId?: string | null
  selectedNoteId?: string | null
  patientIdSource?: string
  contactIdSource?: string
  sourceIdSource?: string
  viewOnly?: boolean
  initialFilter?: {
    filterBy?: string
    dateFrom?: string
    dateTo?: string
    category?: string
    item?: string
  }
  showSidePanel?: boolean
  newList?: INote[]
}

const AddNoteModal: React.FC<AddNoteProps> = ({
  show,
  onHide,
  source,
  contextType,
  selectedRefId,
  selectedNoteId,
  patientIdSource,
  contactIdSource,
  viewOnly = false,
  initialFilter,
  showSidePanel = true,
  newList,
}) => {
  const selectedNoteRef = useRef<HTMLButtonElement>(null)
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [inputText] = useState<string>('')
  const [noteDetails, setNoteDetails] = useState<Partial<INote> | null>(null)
  const [activeNote, setActiveNote] = useState<INote | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(true)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [noteList, setNoteList] = useState<INote[]>([])
  const [filteredNotes, setFilteredNotes] = useState<INote[]>([])
  const [appliedFilters, setAppliedFilters] = useState<{
    filterBy: string
    dateFrom: string
    dateTo: string
    category: string
    item: string
  }>({
    filterBy: initialFilter?.filterBy ?? '',
    dateFrom: initialFilter?.dateFrom ?? '',
    dateTo: initialFilter?.dateTo ?? '',
    category: initialFilter?.category ?? '',
    item: initialFilter?.item ?? '',
  })
  const [hasNewOrUpdatedRecord, setHasNewOrUpdatedRecord] =
    useState<boolean>(false)
  const [forceTextEditorUpdate, setForceTextEditorUpdate] =
    useState<boolean>(false)
  const [editorContentKey, setEditorContentKey] = useState<number>(0)
  const [resolvedBodyForDisplay, setResolvedBodyForDisplay] = useState<
    string | null
  >(null)
  const uploadedBlobsRef = useRef<Set<string>>(new Set())
  const contact = useAppSelector((state) =>
    source === NoteContextType.CONTACT || source === 'contact'
      ? state.messageCenter.contact
      : state.patient.selectedContact,
  )
  const patient = useAppSelector((state) =>
    source === NoteContextType.CONTACT || source === 'contact'
      ? state.messageCenter.patient
      : state.patient.selectedPatient,
  )
  const appointmentData = useAppSelector(
    (state) => state.appointment?.data ?? [],
  )
  const dentalCharts = useAppSelector(
    (state) => state.charting.dentalCharts ?? [],
  )
  const perioCharts = useAppSelector(
    (state) => state.charting.perioCharts ?? [],
  )
  const treatmentPlans = useAppSelector(
    (state) => state.charting.treatmentPlans ?? [],
  )
  const contextLookup: NoteFilterContextLookup = useMemo(
    () => ({
      appointments: appointmentData.map((a) => ({
        appointment_id: a.appointment_id,
        appt_date: a.appt_date,
        appt_start: a.appt_start,
        appointment_start_date: a.appointment_start_date,
        appointment_start_date_time: a.appointment_start_date,
      })),
      dentalCharts: dentalCharts.map((c) => ({
        chart_id: c.chart_id,
        title: c.title,
        date_time_created_at: c.date_time_created_at,
      })),
      perioCharts: perioCharts.map((c) => ({
        chart_id: c.chart_id,
        title: c.title,
        date_time_created_at: c.date_time_created_at,
      })),
      treatmentPlans: treatmentPlans.map((t) => ({
        tp_id: t.tp_id,
        tp_heading: t.tp_heading,
        date_time_created_at: t.date_time_created_at,
      })),
    }),
    [appointmentData, dentalCharts, perioCharts, treatmentPlans],
  )

  const fetchNotes = async (noteId?: string): Promise<void> => {
    if (!showSidePanel) return
    const contactId = contactIdSource ?? contact?.contact_id
    const patientId = patientIdSource ?? patient?.patient_id
    const resolvedContextType = contextType ?? getContextType(source)
    const contextId = selectedRefId ?? ''
    try {
      showLoading({
        container: '.add-note-modal .modal-body',
      })
      const notes = await dispatch(
        fetchAllNotes({
          contactId,
          patientId,
          contextType: resolvedContextType,
          contextId,
        }),
      ).unwrap()

      let processedNotes = notes ?? []

      if (newList && newList.length > 0) {
        const refIdFromNewList = (newList[0] as Partial<INote>).context_id
        processedNotes = processedNotes.filter(
          (note) => note.context_id !== refIdFromNewList,
        )
        processedNotes = [...processedNotes, ...newList]
      }
      setNoteList(processedNotes)

      if (noteId) {
        const newNote = processedNotes.find((note) => note.note_id === noteId)
        if (newNote) {
          setActiveNote(newNote)
          setNoteDetails(newNote)
        }
      } else {
        setFilteredNotes(processedNotes)
        if (viewOnly && processedNotes.length > 0) {
          setTimeout(() => {
            setIsEditing(false)
            setActiveNote(processedNotes[0])
            setNoteDetails(processedNotes[0])
          }, 300)
        }
      }
    } catch (error) {
      logger.error('Error fetching notes:', error)
    } finally {
      hideLoading('.add-note-modal .modal-body')
    }
  }

  const handleResetActiveNote = (): void => {
    setNoteDetails(activeNote)
    setForceTextEditorUpdate(true)
  }

  const handleResetForm = (): void => {
    setActiveNote(null)
    setNoteDetails(null)
  }

  const handleDeleteNote = async (
    _source: NoteContextType | string,
    note_id?: string,
  ): Promise<void> => {
    if (note_id) {
      const resolvedSourceType = getContextType(_source)
      try {
        showLoading({
          container: '.add-note-modal .modal-body',
        })
        await dispatch(removeNote(note_id)).unwrap()

        if (
          resolvedSourceType === NoteContextType.APPOINTMENT ||
          _source === 'appointment'
        ) {
          fetchNotes()
          handleResetForm()
          setHasNewOrUpdatedRecord(true)
        } else {
          if (
            contextType === NoteContextType.CHARTING ||
            contextType === NoteContextType.PERIO ||
            _source === 'charting' ||
            _source === 'perio'
          ) {
            dispatch(deleteChartNote({ id: note_id, source: _source }))
          }
          fetchNotes()
          handleResetForm()
        }
      } catch (error) {
        logger.error('Error deleting note:', error)
      } finally {
        hideLoading('.add-note-modal .modal-body')
      }
    }
  }

  const handleSelectNote = (note: INote | null): void => {
    setIsEditing(false)
    setActiveNote(note)
    uploadedBlobsRef.current = new Set()
    setResolvedBodyForDisplay(null)
    setEditorContentKey((k) => k + 1)
    if (note) {
      setNoteDetails(note)
    } else {
      setNoteDetails({
        context_type: contextType ?? getContextType(source),
        title: '',
        body: '',
      })
      setIsEditing(true)
    }
  }

  const onNewNoteButtonClick = (): void => {
    setIsEditing(true)
    setActiveNote(null)
    setNoteDetails({
      context_type: getContextType(source),
      title: '',
      body: '',
    })
  }

  const onApply = (
    filters: {
      filterBy: string
      dateFrom: string
      dateTo: string
      category: string
      item: string
    },
    resetActiveNote?: boolean,
  ): void => {
    setAppliedFilters(filters)

    let filtered = [...noteList]

    // Filter by search term
    if (searchQuery) {
      filtered = filtered.filter((note) => {
        const body = note.body ?? ''
        return `${note.title} ${body}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      })
    }

    // Filter by filter type (title or note)
    if (filters.filterBy) {
      if (filters.filterBy === 'title') {
        filtered = filtered.filter((note) =>
          note.title.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      } else if (filters.filterBy === 'note') {
        filtered = filtered.filter((note) => {
          const body = note.body ?? ''
          return body.toLowerCase().includes(searchQuery.toLowerCase())
        })
      }
    }

    // Filter by date range
    if (filters.dateFrom) {
      filtered = filtered.filter((note) => {
        const noteDate = new Date(note.date_time_created_at ?? '')
        const fromDate = new Date(filters.dateFrom)
        return noteDate >= fromDate
      })
    }

    if (filters.dateTo) {
      filtered = filtered.filter((note) => {
        const noteDate = new Date(note.date_time_created_at ?? '')
        const toDate = new Date(filters.dateTo)
        toDate.setHours(23, 59, 59, 999) // Set to end of day
        return noteDate <= toDate
      })
    }

    // Filter by category (context_type)
    if (filters.category) {
      filtered = filtered.filter((note) => {
        const noteContextType = note.context_type ?? ''
        return noteContextType.toString() === filters.category
      })
    }

    // Filter by item
    if (filters.item) {
      filtered = filtered.filter((note) => {
        return note.context_id === filters.item
      })
    }

    setFilteredNotes(filtered)
    if (filtered.length === 0) {
      setActiveNote(null)
      setNoteDetails({
        context_type: contextType ?? getContextType(source),
        title: '',
        body: '',
      })
    } else {
      if (resetActiveNote || !activeNote) {
        handleSelectNote(filtered[0])
      }
    }
  }

  const scrollToSelectedNote = (): void => {
    if (selectedNoteRef.current) {
      selectedNoteRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }

  const handleSubmit = async (): Promise<void> => {
    if (!noteDetails) return

    const resolvedContextType =
      noteDetails.context_type ?? contextType ?? getContextType(source)
    const resolvedPatientId =
      patientIdSource || patient?.patient_id || noteDetails.patient_id || ''
    const resolvedContactId =
      contactIdSource || contact?.contact_id || noteDetails.contact_id || ''
    const resolvedContextId = selectedRefId || noteDetails.context_id

    // Resolve img[data-blob] without src before send so the saved body has full URLs
    const bodyToSave =
      typeof noteDetails.body === 'string' &&
      noteDetails.body.includes('data-blob')
        ? await resolveNoteBodyBlobSrc(noteDetails.body)
        : (noteDetails.body ?? '')

    const noteData: Partial<INote> = {
      title: noteDetails.title || '',
      body: bodyToSave,
      context_type: resolvedContextType,
      ...(resolvedContextId ? { context_id: resolvedContextId } : {}),
      ...(resolvedPatientId ? { patient_id: resolvedPatientId } : {}),
      ...(resolvedContactId ? { contact_id: resolvedContactId } : {}),
    }

    if (noteDetails.note_id) {
      try {
        showLoading({
          container: '.add-note-modal .modal-body',
        })
        const updatedNote = await dispatch(
          updateNoteAction({
            noteId: noteDetails.note_id,
            updateData: noteData,
          }),
        ).unwrap()
        const displayNote: INote = { ...updatedNote, body: bodyToSave }
        setTimeout(() => {
          setNoteList((prev) =>
            prev.map((n) =>
              n.note_id === updatedNote.note_id ? displayNote : n,
            ),
          )
          setNoteDetails(displayNote)
          setActiveNote(displayNote)
          setResolvedBodyForDisplay(bodyToSave)
          setEditorContentKey((k) => k + 1)
        }, 0)
        setHasNewOrUpdatedRecord(true)
        if (!showSidePanel) {
          onHide(true)
        }
      } catch (error) {
        logger.error('Error updating note:', error)
      } finally {
        hideLoading('.add-note-modal .modal-body')
      }
    } else {
      try {
        showLoading({
          container: '.add-note-modal .modal-body',
        })
        const newNote = await dispatch(addNote(noteData)).unwrap()
        const displayNote: INote = { ...newNote, body: bodyToSave }
        setTimeout(() => {
          setNoteList((prev) => [...prev, displayNote])
          setNoteDetails(displayNote)
          setActiveNote(displayNote)
          setResolvedBodyForDisplay(bodyToSave)
          setEditorContentKey((k) => k + 1)
        }, 0)
        setHasNewOrUpdatedRecord(true)
        if (!showSidePanel) {
          onHide(true)
        }
      } catch (error: unknown) {
        logger.error('Error creating note:', error)
      } finally {
        hideLoading('.add-note-modal .modal-body')
      }
    }
  }

  // Update filtered notes when search query changes
  useEffect(() => {
    onApply(appliedFilters)
    const currentNote = noteList.find(
      (entry) => entry.note_id === activeNote?.note_id,
    )
    if (currentNote) handleSelectNote(currentNote)
  }, [searchQuery, noteList])

  useEffect(() => {
    const noteContent = noteDetails?.body ?? ''
    const activeContent = activeNote?.body ?? ''

    if (!noteDetails || !activeNote) {
      return
    }

    const isSameContent =
      noteDetails.title === activeNote.title && noteContent === activeContent

    if (isSameContent) {
      // No changes to reset/save state required by current UX; keep effect for future extensibility
    }
  }, [activeNote, noteDetails])

  useEffect(() => {
    if (isEditing && activeNote) {
      handleResetActiveNote()
    }
  }, [isEditing, activeNote])

  useEffect(() => {
    if (show && activeNote) {
      setTimeout(() => {
        scrollToSelectedNote()
      }, 100)
    }
    if (show && !activeNote) {
      setIsEditing(true)
      setNoteDetails({
        context_type: contextType ?? getContextType(source),
        title: '',
        body: '',
      })
    }
  }, [show, activeNote])

  useEffect(() => {
    if (!show) {
      setNoteDetails(null)
    }
  }, [show])

  // Resolve img[data-blob] without src so images display when loading a note
  useEffect(() => {
    if (!activeNote?.body) {
      setResolvedBodyForDisplay(null)
      return
    }
    if (!activeNote.body.includes('data-blob')) {
      setResolvedBodyForDisplay(null)
      return
    }
    let cancelled = false
    resolveNoteBodyBlobSrc(activeNote.body)
      .then((resolved) => {
        if (!cancelled) setResolvedBodyForDisplay(resolved)
      })
      .catch(() => {
        if (!cancelled) setResolvedBodyForDisplay(null)
      })
    return () => {
      cancelled = true
    }
  }, [activeNote?.note_id, activeNote?.body])

  useEffect(() => {
    fetchNotes(selectedNoteId ?? undefined)
  }, [contact, patient, contactIdSource])

  useEffect(() => {
    if (inputText) setNoteDetails((prev) => ({ ...prev, content: inputText }))
  }, [inputText])

  useEffect(() => {
    if (forceTextEditorUpdate) {
      setForceTextEditorUpdate(false)
    }
  }, [forceTextEditorUpdate])

  const handleUpdateNote = (
    data: Array<{
      title?: string
      body?: string
      content?: string
      tag?: string
    }>,
  ): void => {
    if (data.length > 0) {
      const first = data[0]
      setNoteDetails({
        ...noteDetails,
        title: first.title,
        body: first.body ?? first.content,
        ...(first.tag !== undefined && first.tag !== ''
          ? { context_type: first.tag as NoteContextType }
          : {}),
      })
    }
  }

  const microphoneButtons = (): React.ReactNode => {
    return (
      <div className="microphone-buttons">
        <div className="frequency-bar">
          <i className="mgc mgc_sound_line_line" />
        </div>
        <div className="record-stop-buttons">
          <button className="record-button"></button>
          <button className="stop-button"></button>
        </div>
        <div className="microphone-mode-buttons">
          <button className="regular-microphone-button">
            <i className="mgc mgc_mic_fill" />
          </button>
          <button className="ai-microphone-button">
            <i className="mgc mgc_mic_ai_fill" />
          </button>
        </div>
      </div>
    )
  }

  const rightTopEditorButtons = (): React.ReactNode => {
    return (
      <div className="right-top-editor-buttons">
        {!viewOnly && (
          <>
            {isEditing && microphoneButtons()}
            <div onClick={(e) => e.stopPropagation()}>
              <DropDownButtonComponent
                items={[
                  {
                    text: 'Edit note',
                    iconCss: 'mgc_pencil_fill',
                  },
                  {
                    text: 'Delete note',
                    iconCss: 'mgc_delete_2_fill',
                  },
                ]}
                cssClass="add-note-modal-dropdown e-caret-hide act-button"
                iconCss="mgc_more_2_fill mt-0"
                beforeItemRender={(args) => {
                  if (args.item.text === 'Delete note') {
                    args.element.style.color = 'var(--red-500)'
                  }
                }}
                select={(args) => {
                  if (args.item.text === 'Edit note') {
                    setIsEditing(true)
                  } else if (args.item.text === 'Delete note') {
                    setShowDeleteModal(true)
                  }
                }}
                style={{ height: '24px', width: '24px' }}
              />
            </div>
          </>
        )}
        <div className="d-flex flex-row align-items-center">
          <button
            type="button"
            className={`unstyled-button ${
              !isSidePanelOpen
                ? 'mgc_layout_rightbar_close_line'
                : 'mgc_layout_rightbar_open_line'
            }`}
            aria-label={
              isSidePanelOpen
                ? 'Close notes side panel'
                : 'Open notes side panel'
            }
            onClick={() => setIsSidePanelOpen(!isSidePanelOpen)}
          />
        </div>
      </div>
    )
  }

  return (
    <>
      <Modal
        show={show}
        onHide={() => onHide(hasNewOrUpdatedRecord)}
        centered
        enforceFocus={false}
        className="add-note-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Notes</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-2">
          <div className="d-flex h-100">
            <div className="h-100">
              <div
                className="anm-np-body h-100"
                key={`${activeNote?.note_id ?? 'new-note'}${isEditing ? '-editing' : ''}-${editorContentKey}`}
              >
                <NoteTextEditor
                  patientId={patientIdSource!}
                  noteData={{
                    tag: source.toString(),
                    ...(activeNote && {
                      title: activeNote.title,
                      content: resolvedBodyForDisplay ?? activeNote.body ?? '',
                      tag: activeNote.context_type ?? '',
                      date: new Date(activeNote.date_time_created_at ?? ''),
                    }),
                  }}
                  onUpdate={handleUpdateNote}
                  onSave={handleSubmit}
                  viewMode={
                    viewOnly === false
                      ? activeNote
                        ? isEditing
                          ? false
                          : true
                        : false
                      : viewOnly
                  }
                  settingsData={{
                    showCaption: false,
                  }}
                  rightTopEditorButtons={rightTopEditorButtons}
                />
              </div>
            </div>

            {showSidePanel && (
              <div
                className={`anm-side-panel h-100 d-flex flex-column gap-2 ms-2${
                  !isSidePanelOpen ? ' closing' : ''
                }`}
              >
                {!viewOnly && (
                  <div className="mb-2 d-flex flex-row gap-2">
                    <div className="w-100">
                      <Button className="button-secondary w-100">
                        Use Template
                      </Button>
                    </div>
                    <div className="w-100">
                      <Button
                        className="button-primary w-100"
                        onClick={onNewNoteButtonClick}
                      >
                        New Note
                      </Button>
                    </div>
                  </div>
                )}
                <div className="d-flex w-100">
                  <AddNoteFilter
                    searchTerm={searchQuery}
                    onSearchChange={setSearchQuery}
                    onApply={onApply}
                    refList={noteList
                      .map((note) => ({
                        context_type: note.context_type ?? '',
                        context_id: note.context_id ?? null,
                        context_name: note.context_display_name ?? undefined,
                        context_details: note.context_details ?? null,
                        refName:
                          note.context_display_name ??
                          getContextItemDisplayName(
                            note.context_type ?? '',
                            note.context_id ?? null,
                            contextLookup,
                          ),
                      }))
                      .filter(
                        (item, index, self) =>
                          index ===
                          self.findIndex(
                            (n) => n.context_id === item.context_id,
                          ),
                      )}
                    initialFilter={appliedFilters}
                  />
                </div>
                <div className="flex-grow-1 d-flex overflow-auto">
                  <ListGroup
                    className={`anm-note-listgroup${viewOnly ? ' anm-note-listgroup-view' : ''} h-100`}
                  >
                    {filteredNotes.length > 0 ? (
                      [...filteredNotes]
                        .sort((a, b) => {
                          return (
                            new Date(b.date_time_created_at ?? '').getTime() -
                            new Date(a.date_time_created_at ?? '').getTime()
                          )
                        })
                        .map((note) => (
                          <button
                            key={note.note_id}
                            className={`unstyled-button anm-rp-item${note.note_id === activeNote?.note_id ? ' active' : ''}`}
                            onClick={() => handleSelectNote(note)}
                            ref={
                              note.note_id === activeNote?.note_id
                                ? selectedNoteRef
                                : null
                            }
                          >
                            <span className="anm-li-title">
                              <span className="anm-title">{note.title}</span>
                              <span className="anm-date">
                                {format(
                                  new Date(note.date_time_created_at ?? ''),
                                  'MM/dd/yyyy - hh:mm a',
                                )}
                                {note.date_time_created_at !==
                                note.date_time_updated_at
                                  ? ' (edited)'
                                  : ''}
                              </span>
                            </span>
                            <span className="anm-content">
                              {getPreviewContentText(note.body ?? '')}
                            </span>
                            <span className="anm-footer">
                              <span className="anm-id">
                                {note.updated_by?.split('-')[0]}
                              </span>
                              <span className="d-flex flex-row gap-2">
                                <TagBadge tag={note.context_type ?? ''} />
                              </span>
                            </span>
                          </button>
                        ))
                    ) : (
                      <span className="anm-li-empty">No notes found</span>
                    )}
                  </ListGroup>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            onClick={() => onHide(hasNewOrUpdatedRecord)}
            className="button-secondary"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        backdropClassName="note-overlay-confirmation-backdrop"
        title="Delete note?"
        body={<p>Are you sure you want to delete this note?</p>}
        footer={
          <>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => {
                handleDeleteNote(
                  activeNote?.context_type ?? source,
                  activeNote?.note_id,
                )
                setShowDeleteModal(false)
              }}
            >
              Confirm Delete
            </button>
          </>
        }
      />
    </>
  )
}

export default AddNoteModal
